import dataBase from '@/dataBase/database'
import { reportService } from '@/_services'
import { fileService } from './file.service'
import { motivationalReportService } from './motivationalReport.service'
import { authentication } from '../_store/authentication.module'
import { connectWebSocket } from './websocket'

class SyncManager {
  constructor() {
    this._store = null
    this._syncing = false
    this._progress = { done: 0, total: 0, completed: 0 }
    this._units = new Map()
    this._reportDataUnit = new Map()
  }

  _emitProgress() {
    const p = this._progress
    this._store.commit('pointreport/setSyncProgress', {
      done: Math.min(p.done, p.total),
      total: p.total,
      completed: p.completed,
    })
  }

  _advance(unitKey, amount) {
    const unit = this._units.get(unitKey)
    if (!unit || unit.finished) return
    const delta = Math.min(amount, 1 - unit.accrued)
    unit.accrued += delta
    this._progress.done += delta
    this._emitProgress()
  }

  _itemDone(unitKey) {
    const unit = this._units.get(unitKey)
    if (!unit || unit.finished) return
    unit.remaining--
    if (unit.remaining > 0) return
    this._progress.done += 1 - unit.accrued
    unit.accrued = 1
    unit.finished = true
    this._progress.completed++
    this._emitProgress()
  }

  init(store) {
    this._store = store
    this.resetPendingPhotos()

    const handleOnline = async () => {
      this._store.dispatch('user/loadUser').then(data => {
        const offlineMode = localStorage.getItem('offlineMode')
        if (data.offlineMode != offlineMode) {
          this._store.dispatch('user/changeMode', offlineMode != null ? offlineMode : false)
        }
      })
      if (authentication.state.grants.length) {
        connectWebSocket(localStorage.getItem('LoginName'))
      }
      window.dispatchEvent(new CustomEvent('app:online-sync'))
    }

    const handleOffline = () => {
      localStorage.setItem('offlineMode', true)
      this._store.commit('user/setOfflineMode', true)
      window.dispatchEvent(new CustomEvent('app:online-sync'))
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    if (navigator.onLine) {
      handleOnline()
    } else {
      handleOffline()
    }
  }

  async resetPendingPhotos() {
    const pending = await dataBase.photos.where('synced').anyOf([4, 3]).toArray()
    for (const photo of pending) {
      await dataBase.photos.update(photo.id, { synced: 0, offline: true })
    }
    console.log(`[SyncManager] Сброшено ${pending.length} зависших фото`)
  }

  async _buildUnits(pointReports, reportDataItems) {
    const countPhotos = (reportId) =>
      dataBase.photos.where('reportId').equals(String(reportId)).filter(p => p.synced === 0).count()

    this._units = new Map()
    this._reportDataUnit = new Map()

    const ensure = (key) => {
      if (!this._units.has(key)) {
        this._units.set(key, { photos: 0, weight: 0, accrued: 0, remaining: 0, finished: false })
      }
      return this._units.get(key)
    }

    for (const pr of pointReports) {
      const key = String(pr.pointId)
      const unit = ensure(key)
      unit.remaining++
      unit.photos += await countPhotos(key)
    }

    for (const rd of reportDataItems) {
      const key = String(rd.pointId)
      const unit = ensure(key)
      unit.remaining++
      unit.photos += await countPhotos(rd.id)
      this._reportDataUnit.set(rd.id, key)
    }

    for (const unit of this._units.values()) {
      unit.weight = unit.photos > 0 ? 1 / unit.photos : 0
    }

    this._progress = { done: 0, total: this._units.size, completed: 0 }
  }

  async syncAll() {
    if (!navigator.onLine || this._syncing) return
    this._syncing = true
    const t0 = performance.now()
    console.log('[SyncManager] syncAll start')
    this._store.commit('pointreport/setLoadingAsync', true)
    try {
      const pointReports = await dataBase.pointReport.where('synced').equals(0).toArray()
      const reportDataItems = await dataBase.productReportData.where('synced').equals(0).toArray()
      const totalDmpReports = await dataBase.motivationalDmpReport.where('synced').equals(0).count()
      const unsyncedMotivationalDmp = await dataBase.motivationalDmp.where('synced').equals(0).count()

      if (!reportDataItems.length && !pointReports.length && !totalDmpReports && !unsyncedMotivationalDmp) return

      await this._buildUnits(pointReports, reportDataItems)
      this._emitProgress()

      await this._syncDmpReports()
      await this._syncMotivationalDmp()
      await this._syncReportData(reportDataItems)
      await this._syncPointReports(pointReports)

      const p = this._progress
      p.done = p.total
      p.completed = p.total
      this._emitProgress()
    } catch (err) {
      console.error('[SyncManager]', err)
    } finally {
      this._syncing = false
      console.log(`[SyncManager] syncAll done in ${(performance.now() - t0).toFixed(0)} ms`)
      this._store.commit('pointreport/setLoadingAsync', false)
    }
  }

  async _syncReportData(items) {
    for (const item of items) {
      const reportId = String(item.id)
      const unitKey = this._reportDataUnit.get(item.id)
      try {
        const uploadedPhotos = await this._uploadPhotos(reportId, unitKey)
        await reportService.sendReportData({
          projectId: item.projectId,
          tradePointId: item.pointId,
          productId: item.productId,
          reportItemInfo: item.data,
          comment: item.data.comment,
          presense: item.data.presense,
          sync: true,
          frontPhotos: uploadedPhotos.length,
          photos: uploadedPhotos,
        })

        const photos = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4).toArray()
        for (const photo of photos) {
          await dataBase.photos.update(photo.id, { offline: false, synced: 1 })
        }
        await dataBase.productReportData.update(item.id, { synced: 1 })
      } catch (err) {
        const photosToReset = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4 || p.synced === 3).toArray()
        for (const photo of photosToReset) {
          await dataBase.photos.update(photo.id, { offline: true, synced: 0 })
        }
        console.error(`[SyncManager] Ошибка reportData ${item.id}:`, err)
      } finally {
        this._itemDone(unitKey)
      }
    }
  }

  async _syncPointReports(items) {
    for (const item of items) {
      const reportId = String(item.pointId)
      const unitKey = reportId
      try {
        const uploadedPhotos = await this._uploadPhotos(reportId, unitKey)
        await reportService.sendPreReport({
          projectId: item.projectId,
          tradePointId: item.pointId,
          comment: item.comment,
          tempPhotos: uploadedPhotos,
          reportPhoto: item.reportPhoto,
          frontPhotos: uploadedPhotos.length,
          sync: true,
        })

        const photos = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4).toArray()
        for (const photo of photos) {
          await dataBase.photos.update(photo.id, { offline: false, synced: 1 })
        }
        await dataBase.pointReport.update(item.id, { synced: 1 })
      } catch (err) {
        const photosToReset = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4 || p.synced === 3).toArray()
        for (const photo of photosToReset) {
          await dataBase.photos.update(photo.id, { offline: true, synced: 0 })
        }
        console.error('Ошибка отправки отчёта:', err)
      } finally {
        this._itemDone(unitKey)
      }
    }
  }

  async _syncMotivationalDmp() {
    const unsynced = await dataBase.motivationalDmp.where('synced').equals(0).toArray()

    for (const dmp of unsynced) {
      try {
        let motivationalReportServerId
        const motivationalReport = await dataBase.motivationalReport.get(Number(dmp.localMotivationalReportId))
        if (!motivationalReport.serverId) {
          const motivationalReportData = await motivationalReportService.loadMyMotivation(
            motivationalReport.projectId,
            motivationalReport.networkId,
            motivationalReport.pointId
          )
          motivationalReportServerId = motivationalReportData.id
          await dataBase.motivationalReport.update(motivationalReport.id, {
            serverId: motivationalReportServerId,
            synced: 1,
          })
        } else {
          motivationalReportServerId = motivationalReport.serverId
        }

        const data = await motivationalReportService.createDmp(
          { id: dmp.serverId, dmpCategory: dmp.dmpCategory, comment: dmp.comment },
          motivationalReportServerId
        )
        await dataBase.motivationalDmp.update(dmp.id, { serverId: data.id, synced: 1 })
      } catch (error) {
        console.error(`[SyncManager] Ошибка motivationalDmp ${dmp.id}:`, error)
      }
    }
  }

  async _syncDmpReports() {
    const unsynced = await dataBase.motivationalDmpReport.where('synced').equals(0).toArray()

    for (const report of unsynced) {
      let reportId = null
      try {
        const dmp = await dataBase.motivationalDmp.get(Number(report.localDmpId))
        reportId = `${dmp.localMotivationalReportId}_${dmp.id}`

        let serverDmpId
        if (dmp.serverId) {
          serverDmpId = dmp.serverId
        } else {
          let motivationalReportServerId
          const motivationalReport = await dataBase.motivationalReport.get(Number(dmp.localMotivationalReportId))
          if (!motivationalReport.serverId) {
            const reportData = await motivationalReportService.loadMyMotivation(
              motivationalReport.projectId,
              motivationalReport.networkId,
              motivationalReport.pointId
            )
            motivationalReportServerId = reportData.id
            await dataBase.motivationalReport.update(motivationalReport.id, {
              serverId: motivationalReportServerId,
              synced: 1,
            })
          } else {
            motivationalReportServerId = motivationalReport.serverId
          }

          const data = await motivationalReportService.createDmp(
            { id: dmp.serverId, dmpCategory: dmp.dmpCategory, comment: dmp.comment },
            motivationalReportServerId
          )
          serverDmpId = data.id
          await dataBase.motivationalDmp.update(dmp.id, { serverId: serverDmpId, synced: 1 })
        }

        const uploadedPhotos = await this._uploadPhotos(reportId)
        await motivationalReportService.sendReportDmp(serverDmpId, {
          dmpCategory: report.dmpCategory,
          reportPhoto: uploadedPhotos,
          sync: true,
        })

        const photos = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4).toArray()
        for (const photo of photos) {
          await dataBase.photos.update(photo.id, { offline: false, synced: 1 })
        }
        await dataBase.motivationalDmpReport.update(report.id, { synced: 1 })
      } catch (err) {
        if (reportId) {
          const photosToReset = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4 || p.synced === 3).toArray()
          for (const photo of photosToReset) {
            await dataBase.photos.update(photo.id, { offline: true, synced: 0 })
          }
        }
        console.error(`[SyncManager] Ошибка motivationalDmpReport ${report.id}:`, err)
      }
    }
  }

  async _uploadPhotos(reportId, unitKey, concurrency = 5) {
    const photos = await dataBase.photos.where('reportId').equals(String(reportId)).filter(p => p.synced === 0).toArray()

    const unit = unitKey ? this._units.get(unitKey) : null
    const weight = unit ? unit.weight : 0

    const uploaded = []
    const duplicates = []
    const uploadedSlots = new Array(photos.length)

    const worker = async (photo, index) => {
      try {
        const formData = new FormData()
        const blob = new Blob([photo.blob], { type: `image/${photo.extension}` })
        formData.append('files[]', blob, photo.fileName)

        const data = await fileService.uploadFiles(
          formData,
          photo.motivational ? true : false,
          photo.dmp && photo.motivational ? true : false,
          () => {}
        )

        for (const ph of data) {
          ph.dmp = photo.dmp
          if (ph.alreadyLoaded) {
            duplicates.push({
              uuid: ph.uuid,
              extension: ph.extension,
            })
            await dataBase.photos.update(photo.id, { offline: false, synced: 3 })
          } else {
            await dataBase.photos.update(photo.id, { offline: false, synced: 4 })
          }
        }

        if (weight) this._advance(unitKey, weight)

        uploadedSlots[index] = data
      } catch (err) {
        console.error(`[SyncManager] Ошибка загрузки фото ${photo.id}:`, err)
      }
    }

    const queue = photos.map((photo, index) => ({ photo, index }))
    const runnersCount = Math.min(concurrency, queue.length)
    const runners = []
    for (let i = 0; i < runnersCount; i++) {
      runners.push((async () => {
        while (queue.length) {
          const task = queue.shift()
          if (!task) break
          await worker(task.photo, task.index)
        }
      })())
    }
    await Promise.all(runners)
    for (const slot of uploadedSlots) {
      if (slot) uploaded.push(...slot)
    }
    this._store.commit('pointreport/setDuplicatesPhoto', duplicates)
    return uploaded
  }
}

export default new SyncManager()